import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';

import AppPaths from '../../../AppPaths';
import { BillingSubscription } from './SplashPage';
import Button from '../../../components/Button';
import ErrorMessage, { useError } from '../../../components/ErrorMessage';
import { getLoadingOrErrorElement } from '../../../lib/util';
import { ISupplementSubscriptionProduct } from '../../../types';
import Loading from '../../../components/Loading';
import { logInternalError } from '../../../lib/errors';
import { ReactComponent as DownArrow } from '../../../assets/images/icons/arrow_down.svg';
import styles from '../styles/SubscriptionContainer.module.scss';
import SubscriptionAppBar from '../components/SubscriptionAppBar';
import { supplementsManagement as events } from '../../../lib/analytics/events';
import * as types from '../../../types';
import UpdateCadenceModal from './components/UpdateCadenceModal';
import { updateSubscriptionMutation, supplementsSubscriptionsQuery } from '../../../graphql-operations';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import CancelDeterrentConfirmationModal from './components/CancelDeterrentConfirmationModal';
import { CancellationDeterrents } from './util/types';

interface ChangeFrequencyDeterrentState {
  subscription: BillingSubscription;
  currentSubscriptionOption: ISupplementSubscriptionProduct;
  subscriptionOptions: ISupplementSubscriptionProduct[];
}

export default function ChangeFrequencyDeterrent() {
  const { t } = useTranslation();
  const pageName = 'Change Frequency Deterrent';
  const history = useHistory();
  const location = useLocation<ChangeFrequencyDeterrentState>();
  const { subscription, currentSubscriptionOption, subscriptionOptions } = location.state;
  const { error, errorID, setError } = useError();

  // Only possible options are less frequent than current plan.
  const frequencyOptions = subscriptionOptions
    .filter((option) => option.weeksBetweenShipments > currentSubscriptionOption.weeksBetweenShipments)
    .sort((a, b) => a.weeksBetweenShipments - b.weeksBetweenShipments);
  const showDropdown = frequencyOptions.length > 1;

  // Default to most frequent of less frequent options.
  const defaultSubscriptionProduct = frequencyOptions[0];
  const defaultSku = defaultSubscriptionProduct.sku;
  const [selectedSku, setSelectedSku] = useState(defaultSku);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSupplementSubscriptionProduct, setSelectedSupplementSubscriptionProduct] =
    useState(defaultSubscriptionProduct);
  events.pageLoad({ subscriptionId: subscription.id }, pageName);

  const shippingText = () => {
    const selectedOption = subscriptionOptions.find((option) => option.sku === selectedSku);
    return t('ships_every_weeks_weeks', { weeks: selectedOption?.weeksBetweenShipments });
  };

  const [updateMutation, updateMutationState] = useMutation<
    types.gqlTypes.ECOMMERCE_updateSupplementSubscription,
    types.gqlTypes.ECOMMERCE_updateSupplementSubscriptionVariables
  >(updateSubscriptionMutation, {
    refetchQueries: [{ query: supplementsSubscriptionsQuery }],
    onCompleted: (data) => {
      const success = !!data.updateSupplementSubscription?.success;
      if (success) {
        history.push({
          pathname: AppPaths.Supplements.Root,
          state: { toast: { success: true, change: 'updated' }, refetch: false },
          search: window.location.search,
        });
        events.shipmentFrequencyUpdated({
          subscriptionId: subscription.id,
          oldSku: currentSubscriptionOption.sku,
          newSku: selectedSku,
        });
      }
      setError(
        data.updateSupplementSubscription?.reasonForFailure
          ? t('failed_to_update_subscription_reason', { reason: data.updateSupplementSubscription?.reasonForFailure })
          : t('failed_to_update_subscription'),
      );
    },
    onError(err) {
      setError(err.message);
      logInternalError(err);
    },
  });

  const updateLoading = getLoadingOrErrorElement(updateMutationState.loading, null);

  const handleSelection = (sku: string) => {
    setModalOpen(false);
    setSelectedSku(sku);
    const selectedOption = subscriptionOptions.find((option) => option.sku === selectedSku);
    setSelectedSupplementSubscriptionProduct(selectedOption!);
  };

  const handleUpdate = () => {
    updateMutation({
      variables: {
        input: {
          newSku: selectedSku,
          recurlySubscriptionId: subscription.id,
        },
      },
    });
  };

  const handleCancel = () => {
    events.deterrentDeclined({ subscriptionId: subscription.id }, CancellationDeterrents.UPDATE);
    history.push({
      pathname: AppPaths.Supplements.Discount(subscription.id),
      state: { subscription },
      search: window.location.search,
    });
  };

  const formattedNextShipDatePreview = () => {
    const preview = subscription.supplementShipmentDates!.previewUpdate.find(
      (previewDate) => previewDate.sku === selectedSku,
    );
    if (!preview) {
      return '';
    }

    return DateTime.fromISO(preview.nextShipDate).toFormat('cccc, LLL d');
  };

  const previewNextShipDateText = () => {
    if (!formattedNextShipDatePreview) {
      return '';
    }
    return (
      <>
        {t('after_updating_the_date_of_your_next_shipment_will_be')}{' '}
        <span className={styles.emphasize}>{`${formattedNextShipDatePreview()}.`}</span>
      </>
    );
  };

  const confirmationModalBody = t(
    'your_shipment_frequency_will_be_updated_to_every_weeks_weeks_and_your_next_supplement_order_will_ship_on_date',
    {
      weeks: subscription.subscriptionOption.weeksBetweenShipments,
      date: formattedNextShipDatePreview(),
    },
  );

  return (
    <>
      <div className={classNames(styles.fullWidthWrapper, styles.updatedFullWidthWrapper)}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() => events.goBackFromPage({ subscriptionId: subscription.id }, pageName)}
          closeButtonAnalyticsEvent={() => events.closePage({ subscriptionId: subscription.id }, pageName)}
        />
        <div className={styles.subscriptionContainer}>
          <div className={styles.largeHeader}>
            <h1>{t('update_shipment_frequency')}</h1>
          </div>

          {updateLoading ? (
            <Loading className={styles.loading} />
          ) : (
            <div className={styles.content}>
              <p>
                {t(
                  'were_happy_to_change_how_often_you_receive_your_supplement_shipments_youre_currently_receiving_them_every_weeks_weeks',
                  {
                    weeks: currentSubscriptionOption.weeksBetweenShipments,
                  },
                )}
              </p>

              {showDropdown ? (
                <UpdateCadenceModal
                  trigger={
                    <div className={classNames(styles.highlight, styles.rows)}>
                      <div>
                        <div className={styles.header}>{t('update_shipment_frequency')}</div>
                        <div className={styles.title}>{shippingText()}</div>
                      </div>
                      <div>
                        <DownArrow />
                      </div>
                    </div>
                  }
                  subscription={subscription}
                  currentSubscriptionOption={selectedSupplementSubscriptionProduct}
                  subscriptionOptions={frequencyOptions}
                  onContinue={(sku) => {
                    setModalOpen(true);
                    handleSelection(sku);
                  }}
                  open={modalOpen}
                />
              ) : (
                <div className={styles.highlight}>
                  <div className={styles.header}>{t('update_shipment_frequency')}</div>
                  <div className={styles.title}>{shippingText()}</div>
                </div>
              )}
              <p>{previewNextShipDateText()}</p>
            </div>
          )}
          {error && <ErrorMessage errors={[error]} errorID={errorID} />}
        </div>
        <div className={classNames(styles.buttonContainer, styles.updatedButtonContainer)}>
          <CancelDeterrentConfirmationModal
            onContinue={handleUpdate}
            header={t('confirm_updated_frequency')}
            body={confirmationModalBody}
            trigger={<Button className={styles.button}>{t('update_shipment_frequency')}</Button>}
            deterrent={CancellationDeterrents.UPDATE}
          />

          <Button className={styles.button} onClick={handleCancel} tertiary>
            {t('continue_with_cancellation')}
          </Button>
        </div>
      </div>
    </>
  );
}
